"use client";

import React from "react";
import { InfoText } from "@/components/InfoText";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { Info } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BASE_COVERAGES, BaseCoverageId } from "@/config/coverages";

interface CoverageSelectionProps {
  coverageSelections: Partial<Record<BaseCoverageId, boolean>>;
  setCoverageSelections: (next: Partial<Record<BaseCoverageId, boolean>>) => void;
  error: string;
  isLoading: boolean;
  groupRef: React.RefObject<HTMLDivElement>;
  hideTitle?: boolean;
}

const CoverageSelection: React.FC<CoverageSelectionProps> = ({
  coverageSelections,
  setCoverageSelections,
  error,
  isLoading,
  groupRef,
  hideTitle = false,
}) => {
  const { t } = useTranslation();

  const toggleCoverage = (id: BaseCoverageId, checked: boolean) => {
    const next = { ...coverageSelections, [id]: checked };
    // Gated coverages (e.g. Vorerkrankung) can't stay selected once what they require is unchecked.
    for (const def of BASE_COVERAGES) {
      if (def.requires && !next[def.requires]) {
        next[def.id] = false;
      }
    }
    setCoverageSelections(next);
  };

  return (
    <Card className={cn("w-full mx-auto border-none shadow-none", hideTitle ? "max-w-6xl" : "max-w-4xl")} ref={groupRef}>
      {!hideTitle && (
        <CardHeader className="px-0">
          <CardTitle className="text-2xl font-bold">{t("coverages.sectionTitle")}</CardTitle>
        </CardHeader>
      )}
      <CardContent className={cn("space-y-3 px-0", hideTitle && "pt-6")}>
        <div className="flex flex-wrap gap-x-3 gap-y-1">
          {BASE_COVERAGES.filter((def) => !def.alwaysRequested && !def.neverRequested).map((def) => {
            const checked = !!coverageSelections[def.id];
            const disabled = isLoading || (!!def.requires && !coverageSelections[def.requires]);

            return (
              <div key={def.id} className="flex flex-col gap-0.5 min-w-[180px]">
                <div className="flex items-center gap-2">
                  {/* Only checkbox + label toggle. The info dialog is a sibling: its portal (overlay,
                   * close button) bubbles React events to the parent, which would toggle the checkbox. */}
                  <div
                    onClick={() => !disabled && toggleCoverage(def.id, !checked)}
                    className={cn(
                      "flex items-center gap-2",
                      disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                    )}
                  >
                    <Checkbox
                      checked={checked}
                      disabled={disabled}
                      onCheckedChange={(value) => toggleCoverage(def.id, value === true)}
                      onClick={(e) => e.stopPropagation()}
                      className="rounded-[4px]"
                      style={{ borderRadius: "4px" }}
                    />
                    <span className="font-bold text-base whitespace-nowrap">{t(def.labelKey)}</span>
                  </div>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-6 w-6">
                        <Info className="h-5! w-5! text-gray-500" />
                        <span className="sr-only">{t("common.moreInfo")}</span>
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{t(def.infoTitleKey)}</DialogTitle>
                        <InfoText text={t(def.infoDescriptionKey)}>
                          {def.requires && (
                            <p className="font-medium">
                              {t("coverages.requiresHint", { coverage: t(BASE_COVERAGES.find((c) => c.id === def.requires)!.labelKey) })}
                            </p>
                          )}
                        </InfoText>
                      </DialogHeader>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            );
          })}
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
      </CardContent>
    </Card>
  );
};

export default CoverageSelection;
